import pick from 'lodash/pick';
import { retrieveStripeSubscription,
         retrieveStripeCheckoutSession,
         createStripeCheckoutSession,
         updateStripeSubscription,
         deliverCustomEmail } from '../../util/api';

import { storableError } from '../../util/errors';
import { loadStripe } from '@stripe/stripe-js';
import { stringToLongHash } from '../../util/string';
import { customEmailData } from '../../util/customEmailData';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// ================ Action types ================ //
export const SET_INITIAL_STATE = 'app/SubscriptionPlansPage/SET_INITIAL_STATE';

export const VERIFY_SUBSCRIPTION_REQUEST = 'app/SubscriptionPlansPage/VERIFY_SUBSCRIPTION_REQUEST';
export const VERIFY_SUBSCRIPTION_SUCCESS = 'app/SubscriptionPlansPage/VERIFY_SUBSCRIPTION_SUCCESS';
export const VERIFY_SUBSCRIPTION_ERROR = 'app/SubscriptionPlansPage/VERIFY_SUBSCRIPTION_ERROR';

export const START_CHECKOUT_REQUEST = 'app/SubscriptionPlansPage/START_CHECKOUT_REQUEST';
export const START_CHECKOUT_ERROR = 'app/SubscriptionPlansPage/START_CHECKOUT_ERROR';
export const START_CHECKOUT_SUCCESS = 'app/SubscriptionPlansPage/START_CHECKOUT_SUCCESS';

export const UPDATE_SUBSCRIPTION_REQUEST = 'app/SubscriptionPlansPage/UPDATE_SUBSCRIPTION_REQUEST';
export const UPDATE_SUBSCRIPTION_SUCCESS = 'app/SubscriptionPlansPage/UPDATE_SUBSCRIPTION_SUCCESS';
export const UPDATE_SUBSCRIPTION_ERROR = 'app/SubscriptionPlansPage/UPDATE_SUBSCRIPTION_ERROR';

// ================ Reducer ================ //

const initialState = {
  verifySubscriptionInProgress: false,
  verifySubscriptionError: null,
  subscription: {},
  startCheckoutInProgress: false,
  startCheckoutError: null,
  checkoutCompleted: false,
  updateSubscriptionInProgress: false,
  updateSubscriptionError: null,
  updateSubscriptionSuccess: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      return { ...initialState, ...payload };
    case VERIFY_SUBSCRIPTION_REQUEST:
      return { ...state, verifySubscriptionInProgress: true, verifySubscriptionError: null };
    case VERIFY_SUBSCRIPTION_SUCCESS:
      return { ...state, verifySubscriptionInProgress: false, verifySubscriptionError: null, subscription: payload.subscription };
    case VERIFY_SUBSCRIPTION_ERROR:
      return { ...state, verifySubscriptionInProgress: false, verifySubscriptionError: payload.error };

    case START_CHECKOUT_REQUEST:
      return { ...state, startCheckoutInProgress: true, startCheckoutError: null };
    case START_CHECKOUT_SUCCESS:
      return { ...state, startCheckoutInProgress: false, startCheckoutError: null, checkoutCompleted: true };
    case START_CHECKOUT_ERROR:
      return { ...state, startCheckoutInProgress: false, startCheckoutError: payload.error };

    case UPDATE_SUBSCRIPTION_REQUEST:
      return { ...state, updateSubscriptionInProgress: true, updateSubscriptionError: null, updateSubscriptionSuccess: false };
    case UPDATE_SUBSCRIPTION_SUCCESS:
      return { ...state, updateSubscriptionInProgress: false, updateSubscriptionError: null, updateSubscriptionSuccess: true };
    case UPDATE_SUBSCRIPTION_ERROR:
      return { ...state, updateSubscriptionInProgress: false, updateSubscriptionError: action.payload.error, updateSubscriptionSuccess: false };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_STATE,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const verifySubscriptionRequest = () => ({ type: VERIFY_SUBSCRIPTION_REQUEST });
export const verifySubscriptionSuccess = subscription => ({ type: VERIFY_SUBSCRIPTION_SUCCESS, payload: { subscription } });
export const verifySubscriptionError = error => ({ type: VERIFY_SUBSCRIPTION_ERROR, payload: { error }, error: true });

export const startCheckoutRequest = () => ({ type: START_CHECKOUT_REQUEST });
export const startCheckoutSuccess = () => ({ type: START_CHECKOUT_SUCCESS });
export const startCheckoutError = error => ({ type: START_CHECKOUT_ERROR, payload: { error }, error: true });

export const updateSubscriptionRequest = () => ({ type: UPDATE_SUBSCRIPTION_REQUEST });
export const updateSubscriptionSuccess = subscription => ({ type: UPDATE_SUBSCRIPTION_SUCCESS, payload: { subscription } });
export const updateSubscriptionError = error => ({ type: UPDATE_SUBSCRIPTION_ERROR, payload: { error }, error: true });

// ================ Thunks ================ //

export function verifySubscription(sessionId) {
  return async (dispatch, getState, sdk) => {
    dispatch(verifySubscriptionRequest());

    try {
      // Fetch session and subscription in parallel
      const [{ session }, { subscription }] = await Promise.all([
        retrieveStripeCheckoutSession(sessionId),
        (async () => {
          const { session } = await retrieveStripeCheckoutSession(sessionId);
          return retrieveStripeSubscription(session.subscription);
        })(),
      ]);

      const { id, status, customer, currency, created, plan, items } = subscription;

      const currentPeriod = items?.data?.[0] || {};
      const planData = plan || {};

      const subscriptionData = {
        id,
        status,
        customer,
        currency,
        created,
        current_period_start: currentPeriod.current_period_start,
        current_period_end: currentPeriod.current_period_end,
        plan: {
          id: planData.id,
          amount: planData.amount,
          amount_decimal: planData.amount_decimal,
          interval: planData.interval,
          nickname: planData.nickname,
        },
      };

      const activatedPlan = planData.id === process.env.REACT_APP_STRIPE_GROWER_PRICE_ID ? 'GROWER' : 'PLUS';

      await sdk.currentUser.updateProfile({
        privateData: {
          stripeSubscription: subscriptionData,
        },
        publicData: {
          stripeSubscriptionHash: stringToLongHash(subscription.id),
          billingCycleStart: currentPeriod.current_period_start,
          billingCycleEnd: currentPeriod.current_period_end,
          activatedPlan,
        },
      });

      const { to, subject, text, html } = customEmailData({
        user: getState().user.currentUser,
        planName: activatedPlan,
        event: 'purchase'
      });

      await deliverCustomEmail({ to, subject, text, html });

      dispatch(verifySubscriptionSuccess(subscriptionData));
      return subscriptionData;

    } catch (e) {
      const error = storableError(e);
      dispatch(verifySubscriptionError(error));
      throw error;
    }
  };
}

export function startCheckout({ priceId, customerEmail }) {
  return async (dispatch, getState, sdk) => {
    dispatch(startCheckoutRequest());

    try {
      if (priceId === 'free') {
        await sdk.currentUser.updateProfile({publicData: { activatedPlan: 'FREE' }});

        const { to, subject, text, html } = customEmailData({
          user: getState().user.currentUser,
          planName: 'FREE',
          event: 'purchase'
        });

        await deliverCustomEmail({ to, subject, text, html });
        window.location.href = '/';
        return
      } else {
        const response = await createStripeCheckoutSession({
          priceId,
          customerEmail,
          successUrl: `${window.location.origin}/pricing/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/pricing`,
        });

        const stripe = await stripePromise;
        await stripe.redirectToCheckout({ sessionId: response.sessionId });
      }
    } catch (e) {
      const error = storableError(e);
      dispatch(startCheckoutError(error));
      throw error;
    }
  };
}

export function updateSubscription({ subscriptionId, priceId }) {
  return async (dispatch, getState, sdk) => {
    dispatch(updateSubscriptionRequest());

    try {
      const { subscription } = await updateStripeSubscription(subscriptionId, { priceId });

      const { id, status, customer, currency, created, plan, items } = subscription;

      const currentPeriod = items?.data?.[0] || {};
      const planData = plan || {};

      const subscriptionData = {
        id,
        status,
        customer,
        currency,
        created,
        current_period_start: currentPeriod.current_period_start,
        current_period_end: currentPeriod.current_period_end,
        plan: {
          id: planData.id,
          amount: planData.amount,
          amount_decimal: planData.amount_decimal,
          interval: planData.interval,
          nickname: planData.nickname,
        },
      };

      const activatedPlan = planData.id === process.env.REACT_APP_STRIPE_GROWER_PRICE_ID ? 'GROWER' : 'PLUS';

      const updateResp = await sdk.currentUser.updateProfile({
        privateData: {
          stripeSubscription: subscriptionData,
        },
        publicData: {
          stripeSubscriptionHash: stringToLongHash(subscription.id),
          billingCycleStart: currentPeriod.current_period_start,
          billingCycleEnd: currentPeriod.current_period_end,
          activatedPlan,
        },
      });

      const { to, subject, text, html } = customEmailData({
        user: getState().user.currentUser,
        planName: activatedPlan,
        event: 'update'
      });

      await deliverCustomEmail({ to, subject, text, html });

      dispatch(updateSubscriptionSuccess(subscriptionData));
      return subscriptionData;
    } catch (e) {
      const error = storableError(e);
      dispatch(updateSubscriptionError(error));
      throw error;
    }
  };
}
